/**
 * useProjector — Hook for managing the Clean Studio Projector popup window & Picture-in-Picture.
 *
 * Allows zero-latency display of the processed video stream in a clean, borderless
 * window for OBS / Streamlabs Window Capture, or an always-on-top PiP window.
 */

import { useState, useCallback, useRef, useEffect } from "react";

export interface ProjectorControls {
  openProjector: (stream: MediaStream | null) => Window | null;
  closeProjector: () => void;
  updateProjectorStream: (stream: MediaStream | null) => void;
  togglePiP: (videoElement: HTMLVideoElement | null) => Promise<void>;
  isProjectorOpen: boolean;
  isPiPActive: boolean;
}

export function useProjector(): ProjectorControls {
  const [isProjectorOpen, setIsProjectorOpen] = useState(false);
  const [isPiPActive, setIsPiPActive] = useState(false);
  const projectorWinRef = useRef<Window | null>(null);
  const checkTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateProjectorStream = useCallback((stream: MediaStream | null) => {
    (window as any).__STREAM_PERSONA_OUTPUT_STREAM__ = stream;
    if (projectorWinRef.current && !projectorWinRef.current.closed) {
      try {
        projectorWinRef.current.postMessage({ type: "SP_STREAM_UPDATE" }, window.location.origin);
      } catch {
        /* ignore cross-context message errors */
      }
    }
  }, []);

  const closeProjector = useCallback(() => {
    if (projectorWinRef.current && !projectorWinRef.current.closed) {
      projectorWinRef.current.close();
    }
    projectorWinRef.current = null;
    setIsProjectorOpen(false);
    if (checkTimerRef.current) {
      clearInterval(checkTimerRef.current);
      checkTimerRef.current = null;
    }
  }, []);

  const openProjector = useCallback((stream: MediaStream | null): Window | null => {
    updateProjectorStream(stream);

    // If already open, focus it
    if (projectorWinRef.current && !projectorWinRef.current.closed) {
      projectorWinRef.current.focus();
      return projectorWinRef.current;
    }

    const width = Math.min(1280, window.screen.availWidth - 100);
    const height = Math.min(720, window.screen.availHeight - 100);
    const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));

    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      "menubar=no",
      "status=no",
      "toolbar=no",
      "location=no",
      "scrollbars=no",
      "resizable=yes",
    ].join(",");

    const win = window.open("/projector", "StreamPersonaProjector", features);
    if (!win) {
      alert("Popup blocked! Please allow popups for StreamPersona to open the Studio Projector.");
      return null;
    }

    projectorWinRef.current = win;
    setIsProjectorOpen(true);

    if (checkTimerRef.current) clearInterval(checkTimerRef.current);
    checkTimerRef.current = setInterval(() => {
      if (win.closed) {
        setIsProjectorOpen(false);
        projectorWinRef.current = null;
        if (checkTimerRef.current) {
          clearInterval(checkTimerRef.current);
          checkTimerRef.current = null;
        }
      }
    }, 1000);

    return win;
  }, [updateProjectorStream]);

  const togglePiP = useCallback(async (videoElement: HTMLVideoElement | null) => {
    if (!videoElement) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiPActive(false);
      } else if (document.pictureInPictureEnabled && videoElement.requestPictureInPicture) {
        await videoElement.requestPictureInPicture();
        setIsPiPActive(true);

        const handleLeave = () => {
          setIsPiPActive(false);
          videoElement.removeEventListener("leavepictureinpicture", handleLeave);
        };
        videoElement.addEventListener("leavepictureinpicture", handleLeave);
      } else {
        alert("Picture-in-Picture is not supported in this browser. Use the Studio Projector instead.");
      }
    } catch (e) {
      console.warn("[Projector] PiP toggle failed:", e);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
    };
  }, []);

  return {
    openProjector,
    closeProjector,
    updateProjectorStream,
    togglePiP,
    isProjectorOpen,
    isPiPActive,
  };
}
